import { useMemo } from 'react';
import { Box, Grid, Paper, Stack, Typography } from '@mui/material';
import { BarChart, PieChart } from '@mui/x-charts';
import { useTranslation } from 'react-i18next';
import type { Client, Project, TimeEntry } from '@altyor-kronos/shared-types';
import {
  entriesByDayOfWeek,
  entriesByProject,
  hoursFromSeconds,
  rankedByValue,
  topByValue,
} from '../dashboardAggregations';
import { sumDurationSeconds } from '../timeEntries';
import { colors } from '../../../core/theme/theme';

/**
 * Personal aggregation panel for the Kronos Dashboard, Phase 7.x.
 *
 * Replaces the Phase 7 per-project + per-designation + trend chart
 * trio. Layout mirrors the previous iteration captured in the user-
 * shared screenshots:
 *
 *   • Three dark KPI cards: total time (this week), top project,
 *     top client for the week.
 *   • A stacked-by-project bar chart per day of the week (Mon→Sun),
 *     so the user can see "what was my Tuesday made of?" at a glance.
 *   • A donut with the week's project breakdown alongside a ranked
 *     list (project, hours, percentage of the week) — the same data
 *     in two complementary encodings.
 *
 * Data contract: the parent passes the week's entries (already
 * filtered), the week's Monday, and the project + client lookup
 * maps it already maintains for the rest of the page. No Firestore
 * here — keeps the component trivially testable with mocked data.
 */

/**
 * Palette for the project stacks. Cycles through the brand colours
 * plus a few pastels so 8+ projects remain distinguishable without
 * going full rainbow. Order is deliberate: the brand highlights
 * (yellow, red, kronos-camel) render first.
 */
const PROJECT_COLORS = [
  colors.yellow,
  colors.red,
  colors.kronosAccent,
  colors.warmBeige,
  colors.softGreen,
  '#A78BFA', // soft violet
  '#60A5FA', // soft blue
  '#F472B6', // soft pink
  '#FB923C', // soft orange
  '#34D399', // soft mint
];

export interface PersonalAggregationPanelProps {
  /** Entries for the week being visualised. */
  weekEntries: readonly TimeEntry[];
  /** Monday 00:00 local of the week. */
  weekStart: Date;
  projectsById: ReadonlyMap<string, Project>;
  clientsById: ReadonlyMap<string, Client>;
  /** Total seconds logged this month — rendered as the first KPI card. */
  monthSeconds: number;
}

export default function PersonalAggregationPanel(props: PersonalAggregationPanelProps) {
  const { weekEntries, weekStart, projectsById, clientsById, monthSeconds } = props;
  const { t, i18n } = useTranslation();

  // ─── Derived aggregations ─────────────────────────────────────
  const totalSeconds = useMemo(() => sumDurationSeconds(weekEntries), [weekEntries]);

  const byProject = useMemo(() => entriesByProject(weekEntries), [weekEntries]);
  const ranked = useMemo(() => rankedByValue(byProject), [byProject]);
  const topProject = useMemo(() => topByValue(byProject), [byProject]);

  // Top client: collapse by-project → by-client using the project→client map.
  const byClient = useMemo(() => {
    const m = new Map<string, number>();
    for (const [pid, secs] of byProject.entries()) {
      const clientId = projectsById.get(pid)?.clientId;
      if (!clientId) continue;
      m.set(clientId, (m.get(clientId) ?? 0) + secs);
    }
    return m;
  }, [byProject, projectsById]);
  const topClient = useMemo(() => topByValue(byClient), [byClient]);

  const dailyBuckets = useMemo(
    () => entriesByDayOfWeek(weekEntries, weekStart),
    [weekEntries, weekStart],
  );

  // Stable colour mapping: whichever project ranks #1 for the week
  // gets the first palette slot, #2 the second, etc. Keeps the daily
  // stacked chart and the donut visually consistent.
  const projectColorById = useMemo(() => {
    const m = new Map<string, string>();
    ranked.forEach((row, i) => {
      m.set(row.key, PROJECT_COLORS[i % PROJECT_COLORS.length]);
    });
    return m;
  }, [ranked]);

  // Daily stacked: one series per project, `stack: 'week'` groups
  // them into a single stacked bar per day. Bar labels disabled —
  // the tooltip already gives the breakdown and the bars get
  // crowded with labels at 8+ projects.
  const dailySeries = useMemo(
    () =>
      ranked.map((row) => ({
        id: row.key,
        label: projectLabel(projectsById, row.key),
        data: dailyBuckets.map((b) => round1(hoursFromSeconds(b.byProject.get(row.key) ?? 0))),
        color: projectColorById.get(row.key) ?? colors.lightGray,
        stack: 'week' as const,
      })),
    [ranked, dailyBuckets, projectColorById, projectsById],
  );

  const dayLabels = useMemo(
    () => dailyBuckets.map((b) => formatDayLabel(b.dayStart, i18n.language)),
    [dailyBuckets, i18n.language],
  );

  const pieData = useMemo(
    () =>
      ranked.map((row) => ({
        id: row.key,
        value: round1(hoursFromSeconds(row.value)),
        label: projectLabel(projectsById, row.key),
        color: projectColorById.get(row.key) ?? colors.lightGray,
      })),
    [ranked, projectColorById, projectsById],
  );

  // ─── Rendering ────────────────────────────────────────────────
  const totalLabel = formatHours(totalSeconds, i18n.language);
  const topProjectLabel = topProject
    ? projectLabel(projectsById, topProject.key)
    : t('kronos.dashboard.noData');
  const topClientLabel = topClient
    ? clientsById.get(topClient.key)?.name ?? topClient.key
    : t('kronos.dashboard.noData');

  return (
    <Box data-testid="personal-aggregation-panel">
      {/* ── 4 dark KPI cards on one row ───────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 12, md: 6, lg: 3 }}>
          <DarkKpi
            label={t('kronos.dashboard.kpiMonth')}
            value={formatHours(monthSeconds, i18n.language)}
            testId="agg-kpi-month"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 6, lg: 3 }}>
          <DarkKpi
            label={t('kronos.dashboard.totalTime')}
            value={totalLabel}
            testId="agg-kpi-total"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 6, lg: 3 }}>
          <DarkKpi
            label={t('kronos.dashboard.topProject')}
            value={topProjectLabel}
            testId="agg-kpi-top-project"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 6, lg: 3 }}>
          <DarkKpi
            label={t('kronos.dashboard.topClient')}
            value={topClientLabel}
            testId="agg-kpi-top-client"
          />
        </Grid>
      </Grid>

      {/* ── Daily stacked bar chart ──────────────────────────── */}
      <Paper
        variant="outlined"
        sx={{ p: 3, mb: 3, borderColor: colors.lightGray, borderRadius: 0, overflow: 'auto' }}
        data-testid="agg-daily-stacked"
      >
        <Typography variant="h4" sx={{ mb: 2 }}>
          {t('kronos.dashboard.dailyBreakdown')}
        </Typography>
        {totalSeconds === 0 ? (
          <EmptyChart label={t('kronos.dashboard.noData')} />
        ) : (
          <Box sx={{ minWidth: 600 }}>
            <BarChart
              height={280}
              margin={{ left: 40, right: 16, top: 24, bottom: 40 }}
              xAxis={[{ scaleType: 'band', data: dayLabels }]}
              series={dailySeries}
              slotProps={{ legend: { sx: { display: 'none' } } }}
            />
          </Box>
        )}
      </Paper>

      {/* ── Donut + ranked list side by side ─────────────────── */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderColor: colors.lightGray,
              borderRadius: 0,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            data-testid="agg-donut"
          >
            {totalSeconds === 0 ? (
              <EmptyChart label={t('kronos.dashboard.noData')} />
            ) : (
              <Box sx={{ position: 'relative', width: '100%', height: 240 }}>
                <PieChart
                  height={240}
                  margin={{ left: 0, right: 0, top: 0, bottom: 0 }}
                  series={[
                    {
                      data: pieData,
                      innerRadius: 70,
                      outerRadius: 110,
                      paddingAngle: 1,
                      cornerRadius: 0,
                    },
                  ]}
                  slotProps={{ legend: { sx: { display: 'none' } } }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  <Typography sx={{ fontSize: '28px', fontWeight: 900, lineHeight: 1 }}>
                    {totalLabel}
                  </Typography>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper
            variant="outlined"
            sx={{ p: 3, borderColor: colors.lightGray, borderRadius: 0, height: '100%' }}
            data-testid="agg-ranked-list"
          >
            <Typography variant="h4" sx={{ mb: 2 }}>
              {t('kronos.dashboard.projectBreakdown')}
            </Typography>
            {ranked.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('kronos.dashboard.noData')}
              </Typography>
            ) : (
              <Stack spacing={1.2} data-testid="agg-ranked-rows">
                {ranked.map((row) => (
                  <RankedRowView
                    key={row.key}
                    label={projectLabel(projectsById, row.key)}
                    hours={round1(hoursFromSeconds(row.value))}
                    percent={row.percent}
                    color={projectColorById.get(row.key) ?? colors.lightGray}
                    locale={i18n.language}
                  />
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

// ─── Presentational bits ───────────────────────────────────────────

function DarkKpi({ label, value, testId }: { label: string; value: string; testId: string }) {
  return (
    <Paper
      elevation={0}
      square
      // `minWidth: 0` lets the card shrink inside its grid cell so the
      // value can ellipsis instead of being clipped hard (see #42).
      sx={{ bgcolor: colors.darkBg, color: colors.white, p: 3, borderRadius: 0, minWidth: 0 }}
      data-testid={testId}
    >
      <Typography variant="caption" sx={{ color: colors.lightGray, display: 'block' }}>
        {label}
      </Typography>
      <Typography
        title={value}
        sx={{
          fontSize: '24px',
          fontWeight: 900,
          lineHeight: 1.2,
          mt: 1,
          // Constrain to a single line — project codes + names can
          // be long; `ellipsis` keeps the card's layout stable, and the
          // `title` above reveals the full value on hover.
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </Typography>
    </Paper>
  );
}

function EmptyChart({ label }: { label: string }) {
  return (
    <Box sx={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

function RankedRowView({
  label,
  hours,
  percent,
  color,
  locale,
}: {
  label: string;
  hours: number;
  percent: number;
  color: string;
  locale: string;
}) {
  return (
    <Box data-testid="agg-ranked-row">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Box sx={{ width: 10, height: 10, bgcolor: color, flexShrink: 0 }} />
        <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
          {hours.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}h
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0, minWidth: 56, textAlign: 'right' }}>
          {percent.toFixed(2)}%
        </Typography>
      </Box>
      <Box sx={{ height: 4, bgcolor: colors.lightGray }}>
        <Box sx={{ width: `${Math.min(percent, 100)}%`, height: '100%', bgcolor: color }} />
      </Box>
    </Box>
  );
}

// ─── Pure helpers ──────────────────────────────────────────────────

function projectLabel(projectsById: ReadonlyMap<string, Project>, id: string): string {
  const p = projectsById.get(id);
  return p ? `${p.code} — ${p.name}` : id;
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

function formatHours(seconds: number, locale: string): string {
  const h = hoursFromSeconds(seconds);
  return `${round1(h).toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}h`;
}

function formatDayLabel(day: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, { weekday: 'short', day: '2-digit', month: 'short' }).format(day);
}
